import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getWorkOrders, getAssets, getUsers, createWorkOrder, updateWorkOrder, deleteWorkOrder, purgeWorkOrder, restoreWorkOrder, listTaxonomy, scheduleWorkOrder, WAITING_REASONS } from '../../services/api';
import { addTombstone, removeTombstone } from '../../lib/tombstones';
import { SortBy, buildComparator } from '../../components/SortBy';
import { PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, CheckCircle2, AlertTriangle, Clock, Receipt, ScanLine, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { WorkOrderDetailButton, WorkOrderDetailDialog } from '../../components/WorkOrderDetail';
import { TaxonomyPicker } from '../../components/TaxonomyPicker';
import { QrScanner } from '../../components/QrScanner';
import { ListFilterBar } from '../../components/ListFilterBar';

/**
 * Compact SLA status pill for admin-created work orders. Shows:
 *   · Time pill — on-time / Xd late (completed) or due in Xd / Xd over (open)
 *   · Hours pill — Xh under / Xh over (completed) or Xh / Yh (open)
 *
 * Returns `null` for engineer-raised WOs (they have no targets by design)
 * or when the admin didn't fill either optional field.
 */
const SlaIndicator = ({ wo }) => {
  const isAdminRaised = (wo.createdByRole || 'admin') === 'admin';
  const finishBy = wo.finishByDate;
  const hoursTarget = Number(wo.totalHoursEstimate) || 0;
  if (!isAdminRaised || (!finishBy && !hoursTarget)) return null;

  const completed = wo.status === 'completed';
  const pills = [];

  // Time pill — compares completedAt (for completed) or today (for live WOs)
  // against finishByDate. Day-granular diff; positive = late, negative = early.
  if (finishBy) {
    const due = new Date(finishBy);
    const ref = completed && wo.completedAt ? new Date(wo.completedAt) : new Date();
    if (!isNaN(due.getTime()) && !isNaN(ref.getTime())) {
      const diffDays = Math.floor((ref - due) / (24 * 60 * 60 * 1000));
      if (completed) {
        pills.push(
          diffDays <= 0
            ? { key: 'time', tone: 'ok', icon: CheckCircle2, label: diffDays === 0 ? 'On time' : `${-diffDays}d early` }
            : { key: 'time', tone: 'bad', icon: AlertTriangle, label: `${diffDays}d late` },
        );
      } else {
        pills.push(
          diffDays > 0
            ? { key: 'time', tone: 'bad', icon: AlertTriangle, label: `${diffDays}d over` }
            : { key: 'time', tone: diffDays >= -1 ? 'warn' : 'neutral', icon: Clock, label: diffDays === 0 ? 'Due today' : `in ${-diffDays}d` },
        );
      }
    }
  }

  // Hours pill — compares sum(labor.hours) to the estimate.
  if (hoursTarget > 0) {
    const actual = (wo.labor || []).reduce((s, l) => s + Number(l.hours || 0), 0);
    if (completed) {
      const delta = actual - hoursTarget;
      pills.push(
        delta <= 0
          ? { key: 'hours', tone: 'ok', icon: CheckCircle2, label: delta === 0 ? 'On budget' : `${(-delta).toFixed(1)}h under` }
          : { key: 'hours', tone: 'bad', icon: AlertTriangle, label: `${delta.toFixed(1)}h over` },
      );
    } else if (actual > 0) {
      // Live progress — show "3h / 6h" with tone flip once we're past 100%.
      const over = actual > hoursTarget;
      pills.push({
        key: 'hours',
        tone: over ? 'bad' : 'neutral',
        icon: Clock,
        label: `${actual.toFixed(1)}h / ${hoursTarget}h`,
      });
    }
  }

  if (pills.length === 0) return null;
  const toneClass = {
    ok: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    bad: 'bg-red-50 text-red-800 border-red-200',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`wo-sla-${wo.id}`}>
      {pills.map((p) => {
        const Icon = p.icon;
        return (
          <span
            key={p.key}
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${toneClass[p.tone]}`}
            data-testid={`wo-sla-${p.key}-${wo.id}`}
          >
            <Icon className="h-2.5 w-2.5" />
            {p.label}
          </span>
        );
      })}
    </div>
  );
};

const WorkOrders = () => {
  const [orders, setOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [detailWo, setDetailWo] = useState(null);   // clicked row / card → detail dialog
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', assetIds: [], assigneeIds: [],
    customerId: '', siteCustomerId: '', category: 'Mechanical',
    startByDate: '', finishByDate: '', totalHoursEstimate: '',
    billingType: 'hourly', fixedPriceAmount: '',
    // Backlog-only fields — only sent when `unscheduled` is ticked.
    unscheduled: false,
    waitingReason: 'parts',
  });
  // Schedule-an-unscheduled-WO dialog state.
  const [scheduleWo, setScheduleWo] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    dueDate: '', assigneeIds: [], startByDate: '', finishByDate: '',
  });
  const [scheduling, setScheduling] = useState(false);

  const openScheduleDialog = (w) => {
    setScheduleWo(w);
    setScheduleForm({
      dueDate: '',
      assigneeIds: Array.isArray(w.assigneeIds) && w.assigneeIds.length
        ? [...w.assigneeIds]
        : (w.assigneeId ? [w.assigneeId] : []),
      startByDate: '',
      finishByDate: '',
    });
  };

  const doSchedule = async () => {
    if (!scheduleForm.dueDate) { toast.error('Pick a due date'); return; }
    setScheduling(true);
    try {
      await scheduleWorkOrder(scheduleWo.id, scheduleForm);
      toast.success(`Scheduled — ${scheduleWo.id} moved to Open`);
      setScheduleWo(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduling(false);
    }
  };
  const [loading, setLoading] = useState(true);
  // QR scanner state — admin can scan an asset's QR plate from a
  // phone camera and have the WO form pre-populated with the asset
  // and (via the auto-fill chain) its owning customer.
  const [scanOpen, setScanOpen] = useState(false);
  // "Mark completed" dialog state — opens whenever an admin flips a WO's
  // status to `completed` from the row dropdown so they can backdate
  // the close (paper jobs, late entries, etc.). See `updateStatus`.
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeWo, setCompleteWo] = useState(null);
  const [completeDate, setCompleteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [completeBusy, setCompleteBusy] = useState(false);
  const handleScan = (decoded) => {
    setScanOpen(false);
    let assetId = null;
    try {
      const url = new URL(decoded);
      assetId = url.searchParams.get('asset');
    } catch {
      if (decoded && !decoded.includes(' ')) assetId = decoded.trim();
    }
    if (!assetId) { toast.error('QR code did not contain an asset reference'); return; }
    const match = assets.find(a => a.id === assetId);
    if (!match) { toast.error('Asset not found'); return; }
    setForm(f => ({
      ...f,
      // QR scan APPENDS to the current selection so the admin can chain-scan
      // several plates for one visit (e.g. board 1, board 2, board 3 all
      // covered by the same EICR job).
      assetIds: f.assetIds.includes(match.id) ? f.assetIds : [...f.assetIds, match.id],
      customerId: match.customerId || f.customerId,
    }));
    setOpen(true);  // open the New WO dialog if it isn't already
    toast.success(`Linked to ${match.name}`);
  };
  // Categories pulled from the admin-managed taxonomy. Falls back to the
  // historical defaults if the fetch fails so the form still renders.
  const [categories, setCategories] = useState([
    { id: 'tax_seed_mech', label: 'Mechanical' },
    { id: 'tax_seed_elec', label: 'Electrical' },
    { id: 'tax_seed_ppm',  label: 'PPM' },
    { id: 'tax_seed_insp', label: 'Inspection' },
  ]);
  useEffect(() => {
    listTaxonomy('wo_category').then(setCategories).catch(() => { /* keep fallback */ });
  }, []);
  const [searchParams] = useSearchParams();
  // URL-driven filters — used when a stat tile on the Customer Detail page
  // deep-links into this page (e.g. /admin/work-orders?customerId=xxx&status=open).
  // `status=open` expands to "anything not-yet-completed"; `status=outstanding`
  // = engineer-completed but awaiting admin sign-off; `status=closed` = fully
  // signed off. Undefined → no bucket filter (honours the dropdown instead).
  const customerIdFilter = searchParams.get('customerId') || '';
  const bucket = searchParams.get('status') || '';

  const refresh = () => getWorkOrders().then(setOrders);

  // Delete-confirmation dialog state. We replaced the old "click trash → instant fire"
  // flow because the backend (rightly) refuses to delete WOs attached to a live invoice
  // — and on mobile the resulting error toast was barely readable. Now we open a
  // confirm dialog that shows the linked invoice (if any) and offers a one-click
  // "Detach & delete" for draft invoices.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const askDelete = (wo, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setDeleteTarget(wo);
  };

  const confirmDelete = async ({ forceDetach = false } = {}) => {
    if (!deleteTarget) return;
    const wo = deleteTarget;
    setDeleting(true);
    try {
      await deleteWorkOrder(wo.id, { forceDetach });
      // Server confirmed 200 → record a tombstone FIRST so any in-flight
      // or upstream-cached GET that returns this ID will still hide it,
      // then remove from local state IMMEDIATELY for instant UI feedback.
      // Tombstone auto-expires after 24h or when Undo clears it explicitly.
      addTombstone('work_orders', wo.id);
      setOrders((prev) => prev.filter((x) => x.id !== wo.id));
      setDeleteTarget(null);
      toast.success(`${wo.id} deleted${forceDetach ? ' (detached from invoice)' : ''}`, {
        duration: 6000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await restoreWorkOrder(wo.id);
              removeTombstone('work_orders', wo.id);
              const fresh = await getWorkOrders();
              setOrders(fresh);
              toast.success('Restored');
            } catch {
              toast.error('Restore failed');
            }
          },
        },
      });
      // Best-effort background re-sync — if the server says the WO is
      // still present, surface that as a console warning so we can debug.
      getWorkOrders().then((fresh) => {
        if (fresh.some((x) => x.id === wo.id)) {
          console.warn(`[wo-delete] ${wo.id} still in list after delete — server may not have committed soft-delete`);
        }
        setOrders(fresh);
      }).catch(() => { /* keep optimistic state */ });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = (wo, e) => askDelete(wo, e);

  // Hard-purge escape hatch. Some WOs stubbornly re-appear after a
  // regular soft-delete — usually because a CDN or SW cache is holding
  // an old list response. Purge deletes the document outright so the
  // ghost can't come back regardless of what any cache says.
  const confirmPurge = async ({ forceDetach = false } = {}) => {
    if (!deleteTarget) return;
    const wo = deleteTarget;
    setDeleting(true);
    try {
      await purgeWorkOrder(wo.id, { forceDetach });
      addTombstone('work_orders', wo.id);
      setOrders((prev) => prev.filter((x) => x.id !== wo.id));
      setDeleteTarget(null);
      toast.success(`${wo.id} permanently deleted`, { duration: 4000 });
      getWorkOrders().then((fresh) => {
        if (fresh.some((x) => x.id === wo.id)) {
          console.warn(`[wo-purge] ${wo.id} still in list after purge — server did not commit`);
        }
        setOrders(fresh);
      }).catch(() => { /* keep optimistic state */ });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Permanent delete failed');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    Promise.all([getWorkOrders(), getAssets(), getUsers()])
      .then(([w, a, u]) => { setOrders(w); setAssets(a); setUsers(u); })
      .finally(() => setLoading(false));
  }, []);

  const employees = users.filter(u => u.role === 'employee' || u.role === 'subcontractor');
  const customers = users.filter(u => u.role === 'customer');

  const statusCounts = useMemo(() => {
    // Only reflect the URL-driven `customerId` / `bucket` filters —
    // status chips should still count against that already-scoped set.
    const scoped = orders.filter((w) => {
      if (customerIdFilter && w.customerId !== customerIdFilter) return false;
      if (bucket === 'open' && w.status === 'completed') return false;
      if (bucket === 'outstanding' && !(w.status === 'completed' && w.needsAdminReview)) return false;
      if (bucket === 'closed' && !(w.status === 'completed' && !w.needsAdminReview)) return false;
      return true;
    });
    return {
      all: scoped.length,
      open: scoped.filter((w) => w.status === 'open').length,
      in_progress: scoped.filter((w) => w.status === 'in_progress').length,
      completed: scoped.filter((w) => w.status === 'completed').length,
      overdue: scoped.filter((w) => w.status === 'overdue').length,
      unscheduled: scoped.filter((w) => w.status === 'unscheduled').length,
    };
  }, [orders, customerIdFilter, bucket]);

  const filtered = orders.filter(w => {
    const m = w.title.toLowerCase().includes(search.toLowerCase()) || w.id.toLowerCase().includes(search.toLowerCase());
    const s = statusFilter === 'all' || w.status === statusFilter;
    if (customerIdFilter && w.customerId !== customerIdFilter) return false;
    // Bucketed deep-links from customer page stat tiles.
    if (bucket === 'open' && w.status === 'completed') return false;
    if (bucket === 'outstanding' && !(w.status === 'completed' && w.needsAdminReview)) return false;
    if (bucket === 'closed' && !(w.status === 'completed' && !w.needsAdminReview)) return false;
    return m && s;
  });

  // Sort the filtered list per the user's saved preference.
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const sorted = useMemo(() => {
    const getter = (w, key) => {
      switch (key) {
        case 'customer': return (w.customer || '').toLowerCase();
        case 'priority': {
          const order = { critical: 4, high: 3, medium: 2, low: 1 };
          return order[w.priority] || 0;
        }
        case 'status': return (w.status || '').toLowerCase();
        case 'finish': return w.finishByDate ? new Date(w.finishByDate).getTime() : 0;
        case 'created': return w.createdAt ? new Date(w.createdAt).getTime() : 0;
        case 'id':
        default: return String(w.id || '');
      }
    };
    return [...filtered].sort(buildComparator(sort, getter));
  }, [filtered, sort]);

  const create = async () => {
    if (!form.title || form.assigneeIds.length === 0) return toast.error('Title and at least one assignee required');
    // Only send SLA fields if the admin actually filled them in — the
    // backend treats unset / blank as "no SLA target".
    const payload = {
      title: form.title,
      description: form.description,
      priority: form.priority,
      assetIds: form.assetIds,
      assigneeIds: form.assigneeIds,
      customerId: form.customerId,
      siteCustomerId: form.siteCustomerId,
      category: form.category,
    };
    if (form.startByDate) payload.startByDate = form.startByDate;
    if (form.finishByDate) payload.finishByDate = form.finishByDate;
    if (form.totalHoursEstimate !== '' && Number(form.totalHoursEstimate) > 0) {
      payload.totalHoursEstimate = Number(form.totalHoursEstimate);
    }
    // Billing model. Hourly is the default; only send when admin
    // explicitly picked fixed-price so the backend keeps its baseline
    // for engineer-raised jobs.
    if (form.billingType === 'fixed') {
      const amt = Number(form.fixedPriceAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        return toast.error('Enter the agreed price for a fixed-price job.');
      }
      payload.billingType = 'fixed';
      payload.fixedPriceAmount = amt;
    }
    // Backlog mode — mark the WO as unscheduled from the start. The
    // reason chip surfaces on the row + on the detail dialog so anyone
    // seeing it knows why it's parked.
    if (form.unscheduled) {
      payload.status = 'unscheduled';
      if (form.waitingReason) payload.waitingReason = form.waitingReason;
    }
    // Show success the moment the server confirms creation. Then close the
    // dialog + refresh the list out of the try/catch, because a slow refresh
    // on mobile networks was surfacing as "Failed to create" even though the
    // WO existed in the database.
    let created;
    try {
      created = await createWorkOrder(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
      return;
    }
    toast.success(`${created?.id || 'Work order'} created`);
    setOpen(false);
    setForm({
      title: '', description: '', priority: 'medium', assetIds: [], assigneeIds: [],
      customerId: '', siteCustomerId: '', category: 'Mechanical',
      startByDate: '', finishByDate: '', totalHoursEstimate: '',
      billingType: 'hourly', fixedPriceAmount: '',
      unscheduled: false, waitingReason: 'parts',
    });
    try {
      await refresh();
    } catch {
      /* best-effort refresh; next natural render will catch up */
    }
  };

  const updateStatus = async (id, status) => {
    // Picking "completed" opens the backdate-aware dialog instead of
    // PATCHing immediately — admins regularly close paper jobs days or
    // weeks after the engineer was actually on-site, so a today-stamp
    // would skew the monthly margin chart.
    if (status === 'completed') {
      const wo = orders.find(w => w.id === id);
      setCompleteWo(wo || { id });
      setCompleteDate(new Date().toISOString().slice(0, 10));
      setCompleteOpen(true);
      return;
    }
    try {
      await updateWorkOrder(id, { status });
      await refresh();
      toast.success('Status updated');
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const confirmComplete = async () => {
    if (!completeWo) return;
    if (!completeDate) return toast.error('Pick a completion date');
    setCompleteBusy(true);
    try {
      await updateWorkOrder(completeWo.id, { status: 'completed', completedAt: completeDate });
      await refresh();
      const today = new Date().toISOString().slice(0, 10);
      toast.success(
        completeDate === today ? 'Marked completed' : `Marked completed on ${completeDate}`,
      );
      setCompleteOpen(false);
      setCompleteWo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setCompleteBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Work Orders"
        description="Create, assign, and monitor every job."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/admin/work-orders/duplicates"
              className="inline-flex items-center gap-2 text-xs font-medium text-amber-700 hover:text-amber-800 border border-amber-200 hover:bg-amber-50 rounded px-3 py-2"
              data-testid="wo-duplicates-link"
            >
              Find duplicates
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> New Work Order</Button></DialogTrigger>
            <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader><DialogTitle>Create Work Order</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Replace conveyor motor" /></div>
                <div><Label>Description</Label><Textarea className="mt-1.5" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category</Label>
                    <TaxonomyPicker
                      kind="wo_category"
                      value={form.category}
                      onChange={(v) => setForm({ ...form, category: v })}
                      placeholder="Select a category…"
                      testId="wo-category-picker"
                    />
                  </div>
                </div>
                <div><Label>Customer (billed to)</Label>
                  <Select
                    value={form.customerId || 'none'}
                    onValueChange={v => {
                      const next = v === 'none' ? '' : v;
                      setForm(f => {
                        // Changing customer invalidates an asset pick that
                        // belongs to someone else. Clear it so the admin
                        // can't accidentally link cross-tenant kit.
                        // When customer changes, drop any assets that don't belong
                        // to the newly selected customer.
                        const nextAssetIds = f.assetIds.filter(id => {
                          const a = assets.find(x => x.id === id);
                          return !a || (a.customerId || '') === next;
                        });
                        // If the site was set to the same company we just
                        // picked as customer, snap it back to "same" to
                        // avoid a redundant duplicate.
                        const nextSite = f.siteCustomerId === next ? '' : f.siteCustomerId;
                        return { ...f, customerId: next, siteCustomerId: nextSite, assetIds: nextAssetIds };
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="wo-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Site (physical location)</Label>
                  <Select
                    value={form.siteCustomerId || 'same'}
                    onValueChange={v => setForm(f => ({ ...f, siteCustomerId: v === 'same' ? '' : v }))}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="wo-site-customer-select">
                      <SelectValue placeholder="Same as customer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="same">Same as customer</SelectItem>
                      {customers
                        .filter(c => c.id !== form.customerId)
                        .map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Only set this when the work happens at a different company's site (e.g. subcontracted job).</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Asset</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-700 hover:text-blue-800"
                      onClick={() => setScanOpen(true)}
                      data-testid="admin-wo-scan-qr-btn"
                    >
                      <ScanLine className="h-3.5 w-3.5 mr-1" /> Scan QR
                    </Button>
                  </div>
                  <Select
                    value=""
                    onValueChange={v => {
                      const next = v === 'none' ? '' : v;
                      if (!next) return;
                      setForm(f => {
                        if (f.assetIds.includes(next)) return f;
                        // Auto-fill the customer when picking the FIRST asset —
                        // saves the admin a click. Once a customer's already
                        // set (or another asset is present), leave it alone.
                        const a = assets.find(x => x.id === next);
                        const nextCustomerId = (!f.customerId && f.assetIds.length === 0 && a?.customerId)
                          ? a.customerId
                          : f.customerId;
                        return {
                          ...f,
                          assetIds: [...f.assetIds, next],
                          customerId: nextCustomerId,
                        };
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="wo-asset-select">
                      <SelectValue placeholder={form.assetIds.length ? 'Add another asset' : 'Select asset(s)'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(form.customerId
                        ? assets.filter(a => (a.customerId || '') === form.customerId)
                        : assets
                      )
                        .filter(a => !form.assetIds.includes(a.id))
                        .map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.parentAssetName ? `${a.parentAssetName} › ` : ''}{a.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {form.assetIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wo-asset-chips">
                      {form.assetIds.map(id => {
                        const a = assets.find(x => x.id === id);
                        const label = a
                          ? `${a.parentAssetName ? `${a.parentAssetName} › ` : ''}${a.name}`
                          : id;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                            data-testid={`wo-asset-chip-${id}`}
                          >
                            {label}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({ ...f, assetIds: f.assetIds.filter(x => x !== id) }))}
                              className="h-5 w-5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
                              aria-label={`Remove ${label}`}
                              data-testid={`wo-asset-remove-${id}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {form.customerId && (
                    <p className="text-xs text-slate-500 mt-1">Showing assets for this customer only.</p>
                  )}
                </div>
                <div><Label>Assignees</Label>
                  <Select
                    value=""
                    onValueChange={v => {
                      const next = v === 'none' ? '' : v;
                      if (!next) return;
                      setForm(f => f.assigneeIds.includes(next)
                        ? f
                        : { ...f, assigneeIds: [...f.assigneeIds, next] });
                    }}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="wo-assignee-select">
                      <SelectValue placeholder={form.assigneeIds.length ? 'Add another engineer' : 'Select engineer(s)'} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees
                        .filter(e => !form.assigneeIds.includes(e.id))
                        .map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {form.assigneeIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wo-assignee-chips">
                      {form.assigneeIds.map(id => {
                        const e = employees.find(x => x.id === id);
                        const label = e ? e.name : id;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                            data-testid={`wo-assignee-chip-${id}`}
                          >
                            {label}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({ ...f, assigneeIds: f.assigneeIds.filter(x => x !== id) }))}
                              className="h-5 w-5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
                              aria-label={`Remove ${label}`}
                              data-testid={`wo-assignee-remove-${id}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">Add every engineer working this job — each of them can clock in and their hours all roll onto the same draft invoice.</p>
                </div>

                {/* Billing model. Hourly is the historical default — labour
                    + parts roll into the customer's draft invoice when
                    the job closes. Fixed-price ignores logged hours and
                    bills the agreed amount as a single line. Engineers
                    only ever see jobs as `hourly` so they can keep
                    timing themselves; admins control the toggle. */}
                <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Billing
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, billingType: 'hourly', fixedPriceAmount: '' })}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        form.billingType === 'hourly'
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white hover:border-slate-400'
                      }`}
                      data-testid="wo-billing-hourly-btn"
                    >
                      <div className="font-semibold">Hourly + materials</div>
                      <div className={`text-[11px] mt-0.5 ${form.billingType === 'hourly' ? 'text-slate-200' : 'text-slate-500'}`}>
                        Logged hours &amp; parts bill on close
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, billingType: 'fixed' })}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        form.billingType === 'fixed'
                          ? 'border-emerald-700 bg-emerald-700 text-white'
                          : 'border-slate-200 bg-white hover:border-emerald-500'
                      }`}
                      data-testid="wo-billing-fixed-btn"
                    >
                      <div className="font-semibold">Fixed price</div>
                      <div className={`text-[11px] mt-0.5 ${form.billingType === 'fixed' ? 'text-emerald-50' : 'text-slate-500'}`}>
                        Bill an agreed £ figure on close
                      </div>
                    </button>
                  </div>
                  {form.billingType === 'fixed' && (
                    <div>
                      <Label className="text-[11px] text-slate-600">Agreed price (£)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          placeholder="e.g. 850.00"
                          className="pl-7"
                          value={form.fixedPriceAmount}
                          onChange={e => setForm({ ...form, fixedPriceAmount: e.target.value })}
                          data-testid="wo-fixed-price-input"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">
                        A single line for £{Number(form.fixedPriceAmount || 0).toFixed(2)} will be added to the customer&rsquo;s draft invoice when the job is closed.
                      </p>
                    </div>
                  )}
                </div>

                {/* Backlog toggle — parks the WO as "unscheduled" so
                    it shows up under the Unscheduled tab, out of the
                    day-to-day open list, until admin promotes it. */}
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.unscheduled}
                      onChange={e => setForm({ ...form, unscheduled: e.target.checked })}
                      data-testid="wo-unscheduled-toggle"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        Save to backlog (unscheduled)
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Job exists but has no date yet. Shows up under the
                        Unscheduled tab; won&rsquo;t appear in engineer schedules
                        until you promote it.
                      </div>
                    </div>
                  </label>
                  {form.unscheduled && (
                    <div className="mt-3 pl-6">
                      <Label className="text-[11px] text-slate-600">Reason</Label>
                      <Select
                        value={form.waitingReason}
                        onValueChange={v => setForm({ ...form, waitingReason: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="wo-waiting-reason">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WAITING_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Optional SLA targets — engineers never see these fields
                    on their own create flow, and admin can leave any of
                    them blank. They feed the overview metrics chart. */}
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Targets (optional)
                  </Label>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Fill only what you want tracked. Leave blank to skip and the metric won&rsquo;t count this job.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-[11px] text-slate-600">Start by</Label>
                      <Input
                        type="date"
                        className="mt-1"
                        value={form.startByDate}
                        onChange={e => setForm({ ...form, startByDate: e.target.value })}
                        data-testid="wo-start-by-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-600">Finish by</Label>
                      <Input
                        type="date"
                        className="mt-1"
                        value={form.finishByDate}
                        onChange={e => setForm({ ...form, finishByDate: e.target.value })}
                        data-testid="wo-finish-by-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-600">Total hours</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        placeholder="e.g. 6"
                        className="mt-1"
                        value={form.totalHoursEstimate}
                        onChange={e => setForm({ ...form, totalHoursEstimate: e.target.value })}
                        data-testid="wo-hours-estimate"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="bg-slate-900 hover:bg-slate-800">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <ListFilterBar
        chips={[
          { key: 'all', label: 'All', count: statusCounts.all },
          { key: 'open', label: 'Open', count: statusCounts.open, accent: 'blue' },
          { key: 'in_progress', label: 'In progress', count: statusCounts.in_progress, accent: 'amber' },
          { key: 'unscheduled', label: 'Unscheduled', count: statusCounts.unscheduled, accent: 'slate' },
          { key: 'completed', label: 'Completed', count: statusCounts.completed, accent: 'emerald' },
          { key: 'overdue', label: 'Overdue', count: statusCounts.overdue, accent: 'rose' },
        ]}
        activeChip={statusFilter}
        onChipChange={setStatusFilter}
        query={search}
        onQueryChange={setSearch}
        placeholder="Search work orders by title or id…"
        resultCount={filtered.length}
        totalCount={statusCounts.all}
        resultNoun="work order"
        testIdPrefix="wo"
      />

      <SortBy
        storageKey="sort.workOrders"
        defaultValue="created:desc"
        onChange={setSort}
        testId="wo-sort"
        options={[
          { value: 'id:desc', label: 'WO number — newest first' },
          { value: 'id:asc',  label: 'WO number — oldest first' },
          { value: 'customer:asc',  label: 'Customer (A–Z)' },
          { value: 'customer:desc', label: 'Customer (Z–A)' },
          { value: 'priority:desc', label: 'Priority — highest first' },
          { value: 'priority:asc',  label: 'Priority — lowest first' },
          { value: 'status:asc', label: 'Status (A–Z)' },
          { value: 'finish:asc',  label: 'Finish-by — soonest' },
          { value: 'finish:desc', label: 'Finish-by — latest' },
          { value: 'created:desc', label: 'Created — newest' },
          { value: 'created:asc',  label: 'Created — oldest' },
        ]}
      />


      {/* Desktop / tablet table (≥md). Tap anywhere on the row opens the
          detail dialog — previously the eye icon was the only entry point
          and on narrow screens it scrolled off to the right where users
          couldn't reach it. */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">ID</th>
              <th className="px-4 py-3 font-medium text-slate-600">Title</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Priority</th>
              <th className="px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Assignee</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Asset</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Due / window</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(w => (
              <tr
                key={w._id || w.id}
                onClick={() => setDetailWo(w)}
                className="hover:bg-slate-50 cursor-pointer"
                data-testid={`wo-row-${w.id}`}
                title="Click to open"
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-600 hidden lg:table-cell">{w.id}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{w.title}</div>
                  <div className="text-xs text-slate-500">{w.category}</div>
                  {w.status === 'unscheduled' && w.waitingReason && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-medium">
                        {(WAITING_REASONS.find(r => r.value === w.waitingReason) || {}).label || w.waitingReason}
                      </span>
                    </div>
                  )}
                  <div className="mt-1.5"><SlaIndicator wo={w} /></div>
                  <div className="lg:hidden mt-1.5 flex items-center gap-2 flex-wrap">
                    <PriorityBadge priority={w.priority} />
                    {w.dueDate && <span className="text-[11px] text-slate-500">Due {w.dueDate}</span>}
                  </div>
                  <div className="xl:hidden text-xs text-slate-500 mt-0.5 truncate">
                    {w.assignee}{w.asset ? ` · ${w.asset}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell"><PriorityBadge priority={w.priority} /></td>
                <td className="px-4 py-3"><StatusBadge status={w.status} /></td>
                <td className="px-4 py-3 text-slate-700 hidden lg:table-cell">{w.assignee}</td>
                <td className="px-4 py-3 text-slate-600 hidden xl:table-cell">{w.asset}</td>
                <td className="px-4 py-3 text-slate-600 hidden lg:table-cell" data-testid={`wo-row-dates-${w.id}`}>
                  <div className="flex flex-col gap-0.5 leading-tight">
                    <span>{w.dueDate || '—'}</span>
                    {(w.startByDate || w.finishByDate) && (
                      <span className="text-[11px] text-slate-500" title="Planned start → finish window">
                        {w.startByDate || '—'}
                        <span className="mx-1 text-slate-400">→</span>
                        {w.finishByDate || '—'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    {w.status === 'unscheduled' ? (
                      <Button
                        size="sm"
                        className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
                        onClick={() => openScheduleDialog(w)}
                        data-testid={`wo-schedule-${w.id}`}
                      >
                        <CalendarClock className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Schedule</span>
                      </Button>
                    ) : (
                      <Select value={w.status} onValueChange={(v) => updateStatus(w.id, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`wo-status-${w.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <WorkOrderDetailButton wo={w} onUpdate={refresh} />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={(e) => handleDelete(w, e)}
                      title="Delete work order"
                      data-testid={`wo-delete-${w.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No work orders match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (<md). Full-width tap target for the whole card —
          status dropdown lives in its own row so the action + the open-detail
          gestures don't collide. */}
      <div className="lg:hidden space-y-3">
        {sorted.map(w => (
          <div
            key={w._id || w.id}
            data-testid={`wo-card-${w.id}`}
            onClick={() => setDetailWo(w)}
            className="bg-white border border-slate-200 rounded-md p-4 space-y-3 active:bg-slate-50"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{w.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {w.category}{w.assignee ? ` · ${w.assignee}` : ''}
                  {w.asset ? ` · ${w.asset}` : ''}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <PriorityBadge priority={w.priority} />
                  <StatusBadge status={w.status} />
                  {w.dueDate && <span className="text-[11px] text-slate-500">Due {w.dueDate}</span>}
                </div>
                {(w.startByDate || w.finishByDate) && (
                  <div className="mt-1 text-[11px] text-slate-500" title="Planned start → finish window">
                    <span className="uppercase tracking-wider mr-1 text-slate-400">Window</span>
                    {w.startByDate || '—'}
                    <span className="mx-1 text-slate-400">→</span>
                    {w.finishByDate || '—'}
                  </div>
                )}
                <div className="mt-1.5"><SlaIndicator wo={w} /></div>
              </div>
              <div className="font-mono text-[10px] text-slate-400 shrink-0">{w.id}</div>
            </div>
            {/* Dedicated row for the status selector so tapping it doesn't
                fight the whole-card click. */}
            <div
              className="flex items-center gap-2 pt-2 border-t border-slate-100"
              onClick={(e) => e.stopPropagation()}
            >
              <Select value={w.status} onValueChange={(v) => updateStatus(w.id, v)}>
                <SelectTrigger className="h-9 flex-1 text-sm" data-testid={`wo-status-mobile-${w.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); setDetailWo(w); }}
                data-testid={`wo-open-mobile-${w.id}`}
              >
                Open
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={(e) => handleDelete(w, e)}
                data-testid={`wo-delete-mobile-${w.id}`}
                title="Delete work order"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-md px-4 py-12 text-center text-slate-500">
            No work orders match your filters.
          </div>
        )}
      </div>

      {/* Single detail dialog driven by row/card clicks. */}
      <WorkOrderDetailDialog
        wo={detailWo}
        open={!!detailWo}
        onOpenChange={(v) => { if (!v) setDetailWo(null); }}
        onUpdate={refresh}
      />

      {/* Delete confirmation dialog. Surfaces any linked invoice + its status, and
          offers a one-click "Detach & delete" path when the invoice is still draft. */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}
      >
        <DialogContent data-testid="wo-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete {deleteTarget?.id}?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              {deleteTarget?.title}
            </DialogDescription>
          </DialogHeader>

          {deleteTarget?.invoiceId ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <div className="flex items-center gap-2 font-medium">
                <Receipt className="h-4 w-4" />
                Linked to invoice <span className="font-mono">{deleteTarget.invoiceId}</span>
              </div>
              <div className="text-xs mt-1 leading-snug">
                Tap <span className="font-medium">Detach &amp; delete</span> to remove this work order’s
                lines from the invoice and delete the WO in one step. Sent or paid invoices
                are protected — void them in Xero first.
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              This soft-deletes the work order. You can undo from the toast that follows.
              <div className="text-xs text-slate-500 mt-2">
                If a work order keeps reappearing after delete, use{' '}
                <span className="font-medium text-red-700">Delete permanently</span> — that
                skips the Undo window and removes the record from the database outright.
              </div>
            </div>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              data-testid="wo-delete-cancel"
            >
              Cancel
            </Button>
            {/* Hard-purge escape hatch. When a WO reappears in the list
                after being soft-deleted (usually stale CDN cache or a
                lingering restore call), this permanently removes it. */}
            <Button
              variant="ghost"
              className="text-red-700 hover:text-red-800 hover:bg-red-50 border border-red-200"
              onClick={() => confirmPurge({ forceDetach: !!deleteTarget?.invoiceId })}
              disabled={deleting}
              data-testid="wo-delete-permanent"
              title="Skip the soft-delete + Undo flow and hard-delete the document. Cannot be undone."
            >
              {deleting ? '…' : 'Delete permanently'}
            </Button>
            {deleteTarget?.invoiceId ? (
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => confirmDelete({ forceDetach: true })}
                disabled={deleting}
                data-testid="wo-delete-detach"
              >
                {deleting ? 'Detaching…' : 'Detach & delete'}
              </Button>
            ) : (
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => confirmDelete()}
                disabled={deleting}
                data-testid="wo-delete-confirm"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleScan}
        title="Scan Asset QR"
        hint="Point your camera at the QR plate on the machine to auto-fill the Asset field."
      />

      {/* Schedule an unscheduled WO — pops from the row Schedule button.
          Sets a due date + confirms/reassigns the engineer + flips
          status to 'open' so the job re-enters the normal flow. */}
      <Dialog open={!!scheduleWo} onOpenChange={(v) => { if (!v) setScheduleWo(null); }}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)]" data-testid="wo-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule work order</DialogTitle>
            <DialogDescription>
              {scheduleWo?.title}
              {scheduleWo?.waitingReason && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-medium ml-2">
                  {(WAITING_REASONS.find(r => r.value === scheduleWo.waitingReason) || {}).label || scheduleWo.waitingReason}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Due date</Label>
              <Input
                type="date"
                value={scheduleForm.dueDate}
                onChange={e => setScheduleForm({ ...scheduleForm, dueDate: e.target.value })}
                data-testid="wo-schedule-due"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Assignees</Label>
              <Select
                value=""
                onValueChange={v => {
                  const next = v === 'none' ? '' : v;
                  if (!next) return;
                  setScheduleForm(f => f.assigneeIds.includes(next)
                    ? f
                    : { ...f, assigneeIds: [...f.assigneeIds, next] });
                }}
              >
                <SelectTrigger data-testid="wo-schedule-assignee">
                  <SelectValue placeholder={scheduleForm.assigneeIds.length ? 'Add another engineer' : 'Pick engineer(s)'} />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => (u.role === 'employee' || u.role === 'subcontractor') && !scheduleForm.assigneeIds.includes(u.id))
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {scheduleForm.assigneeIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wo-schedule-assignee-chips">
                  {scheduleForm.assigneeIds.map(id => {
                    const u = users.find(x => x.id === id);
                    const label = u ? u.name : id;
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() => setScheduleForm(f => ({ ...f, assigneeIds: f.assigneeIds.filter(x => x !== id) }))}
                          className="h-5 w-5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
                          aria-label={`Remove ${label}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-slate-600">Start by (optional)</Label>
                <Input
                  type="date"
                  value={scheduleForm.startByDate}
                  onChange={e => setScheduleForm({ ...scheduleForm, startByDate: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Finish by (optional)</Label>
                <Input
                  type="date"
                  value={scheduleForm.finishByDate}
                  onChange={e => setScheduleForm({ ...scheduleForm, finishByDate: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleWo(null)} disabled={scheduling}>Cancel</Button>
            <Button
              onClick={doSchedule}
              disabled={scheduling || !scheduleForm.dueDate}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="wo-schedule-confirm"
            >
              <CalendarClock className="h-4 w-4 mr-2" />
              {scheduling ? 'Scheduling…' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backdate-aware "Mark completed" dialog. Opened by `updateStatus`
          whenever the row dropdown is set to `completed`. Defaults to
          today; admin can pick any past date for paper jobs / late
          entries. Future dates allowed too — useful for scheduled
          retroactive close on a planned PPM. */}
      <Dialog open={completeOpen} onOpenChange={(v) => { setCompleteOpen(v); if (!v) setCompleteWo(null); }}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)]" data-testid="wo-complete-dialog">
          <DialogHeader>
            <DialogTitle>Mark as completed</DialogTitle>
            <DialogDescription>
              {completeWo?.title ? <>Closing <span className="font-semibold">{completeWo.title}</span> ({completeWo.id}).</> : null}
              {' '}Pick the actual completion date — defaults to today, but you can backdate for paper jobs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Completion date</Label>
              <Input
                type="date"
                value={completeDate}
                onChange={(e) => setCompleteDate(e.target.value)}
                className="mt-1.5"
                data-testid="wo-complete-date-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Drives the monthly margin chart and the engineer&rsquo;s timesheet bucket.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCompleteOpen(false); setCompleteWo(null); }}
              disabled={completeBusy}
              data-testid="wo-complete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmComplete}
              disabled={completeBusy || !completeDate}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="wo-complete-confirm"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              {completeBusy ? 'Saving…' : 'Mark completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrders;
